import { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";

// util: crop image → Blob (Helpers)
const createImage = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.addEventListener("load", () => resolve(img));
        img.addEventListener("error", reject);
        img.setAttribute("crossOrigin", "anonymous");
        img.src = url;
    });
const getCroppedImg = async (imageSrc, cropPixels) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = cropPixels.width;
    canvas.height = cropPixels.height;

    ctx.drawImage(
        image,
        cropPixels.x,
        cropPixels.y,
        cropPixels.width,
        cropPixels.height,
        0,
        0,
        cropPixels.width,
        cropPixels.height
    );

    return new Promise((resolve) => {
        canvas.toBlob(
            (blob) => {
                resolve(blob);
            },
            "image/jpeg",
            0.95
        );
    });
};

const EmployeeCrop = ({ open, file, aspect, onClose, onComplete }) => {
    // Main State
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedPixels, setCroppedPixels] = useState(null);
    const [imageUrl, setImageUrl] = useState(null);

    // Reset All State
    useEffect(() => {
        if (!open || !file) return;
        const url = URL.createObjectURL(file);
        setImageUrl(url);
        return () => {
            URL.revokeObjectURL(url);
            setImageUrl(null);
        };
    }, [open, file]);
    useEffect(() => {
        if (open) {
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedPixels(null);
        }
    }, [open]);

    // handlers
    const onCropComplete = useCallback((_, croppedAreaPixels) => {
        setCroppedPixels(croppedAreaPixels);
    }, []);
    const handleDone = async () => {
        try {
            if (!croppedPixels || !imageUrl) {
                toast.warning("Unable to crop image!");
                return;
            }

            const blob = await getCroppedImg(imageUrl, croppedPixels);
            const croppedFile = new File([blob], file.name, {
                type: "image/jpeg",
            });

            onComplete?.(croppedFile);
            onClose?.();
        } catch (err) {
            console.error("Image Crop failed:", err);
            toast.error("Image crop failed!");
        }
    };

    if (!open || !file || !imageUrl) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
            <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden">
                <div className="p-3 border-b text-center font-semibold">
                    Adjust Document
                </div>

                <div className="relative w-full h-[350px] bg-black">
                    <Cropper
                        key={imageUrl}
                        image={imageUrl}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                    />
                </div>

                <div className="px-4 py-3">
                    <label className="block text-xs text-gray-600 mb-1">Zoom</label>
                    <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.01}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        className="w-full"
                    />
                </div>

                <div className="flex justify-between p-3 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-3 py-1.5 text-sm rounded-lg bg-gray-200 text-gray-700"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        disabled={!croppedPixels}
                        onClick={handleDone}
                        className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmployeeCrop;